/**
 * Per-user credential row for one MCP server (YAG-445).
 *
 * Shows whether the signed-in user has bound their own token for this
 * server ("Connected as you") or is riding the shared workspace
 * credential, with an inline connect / replace / disconnect flow against
 * the member-scoped `/api/mcp-servers/:id/credential` endpoints. Token
 * material is write-only: the status endpoint never returns it.
 */
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../ui/button.js";
import { Input } from "../../ui/input.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select.js";

type CredentialStatus =
  | { kind: "loading" }
  | { kind: "error"; error: string }
  | { kind: "unbound" }
  | { kind: "bound"; scheme: string | null; updatedAt: string | null };

type PersonalScheme = "bearer" | "api_key_header";

/** The signed-in person's credential on one server: its status, and the two writes. */
export function useMcpCredential(serverId: string) {
  const [status, setStatus] = useState<CredentialStatus>({ kind: "loading" });

  const load = useCallback(async () => {
    setStatus({ kind: "loading" });
    try {
      const res = await fetch(`/api/mcp-servers/${serverId}/credential`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const body = (await res.json()) as {
        bound: boolean;
        scheme?: string | null;
        updatedAt?: string | null;
      };
      setStatus(
        body.bound
          ? {
              kind: "bound",
              scheme: body.scheme ?? null,
              updatedAt: body.updatedAt ?? null,
            }
          : { kind: "unbound" },
      );
    } catch (err) {
      setStatus({
        kind: "error",
        error: err instanceof Error ? err.message : "Failed to load",
      });
    }
  }, [serverId]);

  useEffect(() => {
    // An empty id is a row reading a status the sheet already holds.
    if (serverId) void load();
  }, [load, serverId]);

  async function disconnect() {
    try {
      const res = await fetch(`/api/mcp-servers/${serverId}/credential`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Disconnect failed (${res.status})`);
      void load();
    } catch (err) {
      setStatus({
        kind: "error",
        error: err instanceof Error ? err.message : "Disconnect failed",
      });
    }
  }

  return { status, load, disconnect };
}

export type McpCredential = ReturnType<typeof useMcpCredential>;

export function McpCredentialRow({
  serverId,
  credential: shared,
}: {
  serverId: string;
  /** The status the sheet's footer also reads; without it the row reads its own. */
  credential?: McpCredential;
}) {
  const own = useMcpCredential(shared ? "" : serverId);
  const { status, load, disconnect } = shared ?? own;
  const [editing, setEditing] = useState(false);
  const [scheme, setScheme] = useState<PersonalScheme>("bearer");
  const [token, setToken] = useState("");
  const [header, setHeader] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setFormError(null);
    try {
      const res = await fetch(`/api/mcp-servers/${serverId}/credential`, {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          authScheme: scheme,
          token,
          ...(scheme === "api_key_header" ? { header } : {}),
        }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? `Save failed (${res.status})`);
      }
      setEditing(false);
      setToken("");
      setHeader("");
      void load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }


  return (
    <div
      className="mb-sm flex flex-col gap-sm rounded-[var(--radius-row)] border border-[var(--print-divider)] p-sm"
      data-testid="mcp-credential-row"
    >
      <div className="flex items-center justify-between gap-md">
        <div className="flex flex-col gap-xs">
          <span className="text-sm font-medium">Your credential</span>
          {status.kind === "loading" && (
            <span className="text-xs text-[var(--text-secondary)]">Loading…</span>
          )}
          {status.kind === "error" && (
            <span role="alert" className="text-xs text-[var(--error)]">
              {status.error}
            </span>
          )}
          {status.kind === "bound" && (
            <span
              className="text-xs text-[var(--text-secondary)]"
              data-testid="mcp-credential-bound"
            >
              Connected as you{status.scheme ? ` (${status.scheme})` : ""}
              {status.updatedAt
                ? ` · updated ${new Date(status.updatedAt).toLocaleString()}`
                : ""}
            </span>
          )}
          {status.kind === "unbound" && (
            <span
              className="text-xs text-[var(--text-secondary)]"
              data-testid="mcp-credential-unbound"
            >
              Your calls use the shared workspace credential. Connect your own
              token so writes carry your authority.
            </span>
          )}
        </div>
        <div className="flex gap-xs">
          {status.kind === "bound" && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="text-xs"
              data-testid="mcp-credential-disconnect"
              onClick={() => void disconnect()}
            >
              Disconnect
            </Button>
          )}
          {(status.kind === "bound" || status.kind === "unbound") && !editing && (
            <Button
              type="button"
              size="sm"
              className="text-xs"
              data-testid="mcp-credential-connect"
              onClick={() => setEditing(true)}
            >
              {status.kind === "bound" ? "Replace token" : "Connect your token"}
            </Button>
          )}
        </div>
      </div>

      {editing && (
        <div
          className="flex flex-wrap items-end gap-sm"
          data-testid="mcp-credential-form"
        >
          <Select
            value={scheme}
            onValueChange={(v) => setScheme(v as PersonalScheme)}
          >
            <SelectTrigger
              className="w-40"
              aria-label="Credential scheme"
              data-testid="mcp-credential-scheme"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bearer">Bearer token</SelectItem>
              <SelectItem value="api_key_header">API key header</SelectItem>
            </SelectContent>
          </Select>
          {scheme === "api_key_header" && (
            <Input
              type="text"
              value={header}
              onChange={(e) => setHeader(e.target.value)}
              placeholder="X-API-Key"
              maxLength={80}
              aria-label="Header name"
              className="w-40"
              data-testid="mcp-credential-header"
            />
          )}
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Your token"
            autoComplete="off"
            aria-label="Your token"
            className="w-56"
            data-testid="mcp-credential-token"
          />
          <Button
            type="button"
            size="sm"
            className="text-xs"
            disabled={saving || token.length === 0}
            data-testid="mcp-credential-save"
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="text-xs"
            onClick={() => {
              setEditing(false);
              setFormError(null);
            }}
          >
            Cancel
          </Button>
          {formError && (
            <span role="alert" className="text-xs text-[var(--error)]">
              {formError}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
